'use client';
import React from 'react';

import styled from '@benzinga/themetron';
import { Watchlist, WatchlistManager } from '@benzinga/watchlist-manager';
import { Modal, Spinner } from '@benzinga/core-ui';
import { safeDelay } from '@benzinga/safe-await';
import { SessionContext } from '@benzinga/session-context';
import { PermissionedComponent } from '@benzinga/user-context';

import styles from './link.module.scss';

const StyledSpinner = styled(Spinner)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

interface Props {
  closeModal: () => void;
  linkedWatchlist: (selectedWatchlist: Watchlist) => void;
}

interface State {
  readonly isLoading: boolean;
  readonly isSyncing: boolean;
  readonly isError: boolean;
  readonly errorMessage: string;
}

export const WatchlistLink: React.FC<Props> = props => (
  <PermissionedComponent
    displayPromptOnDenied={true}
    onPurchasePromptClose={props.closeModal}
    permission={{ action: 'bzpro/widget/use', resource: 'watchlist' }}
  >
    {access => (access ? <WatchlistLinkInternal {...props} /> : null)}
  </PermissionedComponent>
);

export const WatchlistLinkInternal: React.FC<Props> = props => {
  const session = React.useContext(SessionContext);
  const [state, setState] = React.useState<State>({
    errorMessage: '',
    isError: false,
    isLoading: false,
    isSyncing: false,
  });

  React.useEffect(() => {
    if (!window.fastlink) {
      const script = document.createElement('script');

      script.src = 'https://cdn.yodlee.com/fastlink/v4/initialize.js';
      script.type = 'text/javascript';
      script.async = true;

      document.body.appendChild(script);
    }
  }, []);

  const succeeded = React.useCallback(
    async (res: any): Promise<boolean> => {
      setState(prevState => ({
        ...prevState,
        isSyncing: true,
      }));
      try {
        await session.getManager(WatchlistManager).syncYodleeWatchlists();
      } catch (err) {
        console.error('Watchlist Linking - error syncing Yodlee watchlists', err);
      }

      try {
        const watchlists = await session.getManager(WatchlistManager).getWatchlists();
        if (watchlists && watchlists.ok && watchlists.ok.length > 0) {
          const latestWatchlist = watchlists.ok.find(watchlist => {
            return watchlist.name.includes(res.providerName);
          });
          if (latestWatchlist !== undefined && latestWatchlist !== null) {
            const linkedWatchlist = props.linkedWatchlist;
            linkedWatchlist(latestWatchlist);

            setState(prevState => ({
              ...prevState,
              isSyncing: false,
            }));
            return true;
          }
        }
        return false;
      } catch (err) {
        console.error('Watchlist Linking - error caught fetching watchlists', err);
        return false;
      }
    },
    [props.linkedWatchlist, session],
  );

  const close = React.useCallback(
    (err: any) => {
      setState(prevState => ({
        ...prevState,
        errorMessage: '',
        isError: false,
        isSyncing: false,
      }));
      console.error('Watchlist link: sync failed', err);
      const closeModal = props.closeModal;

      closeModal();
    },
    [props.closeModal],
  );

  const onError = React.useCallback((err: any) => {
    setState(prevState => ({
      ...prevState,
      isSyncing: false,
    }));
    const recoverable = err.code === undefined || err.code === null;
    if (!recoverable) {
      setState(prevState => ({
        ...prevState,
        errorMessage: err.message,
        isError: true,
      }));
      console.error('Watchlist Linking - error caught in Yodlee Iframe onError', err.message);
    }
  }, []);

  const onEvent = React.useCallback((_data: any) => {
    setState(prevState => ({
      ...prevState,
      isLoading: false,
    }));
  }, []);

  const attemptSync = React.useCallback(
    async (ev: any, attempts = 0) => {
      setState(prevState => ({
        ...prevState,
        isSyncing: false,
      }));
      if (attempts < 60) {
        const success = await succeeded(ev);
        if (!success) {
          await safeDelay(10000);
          await attemptSync(ev, attempts + 1);
        }
      }
    },
    [succeeded],
  );

  const closeModal = React.useCallback(() => {
    const closeModal = props.closeModal;
    closeModal();
  }, [props.closeModal]);

  const startedToLinKAccount = React.useRef(false);

  const linkAccount = React.useCallback(async () => {
    startedToLinKAccount.current = true;
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    try {
      const authData = await session.getManager(WatchlistManager).getYodleeAccess();

      if (authData.ok && authData.ok.accessToken) {
        await window.fastlink?.close();
        window.fastlink?.open(
          {
            accessToken: authData.ok.accessToken,
            fastLinkURL: authData.ok.fastLinkUrl,
            forceIframe: true,
            onClose: close,
            onError: onError,
            onEvent: onEvent,
            onSuccess: attemptSync,
            params: {
              configName: authData.ok.fastLinkConfigName,
            },
          },
          'container-fastlink',
        );
      }
    } catch (err) {
      console.error('Watchlist Linking - error caught in linkAccount', err);
    }
  }, [attemptSync, close, onError, onEvent, session]);

  React.useEffect(() => {
    if (startedToLinKAccount.current === false) {
      linkAccount();
    }
  }, [linkAccount]);

  return (
    <Modal
      doNotCloseOnBackgroundClick={false}
      hideCrossBtn={true}
      hideTitle={true}
      onClose={closeModal}
      size={{ alert: true }}
      title="Link Brokerage Portfolio"
    >
      <div className={styles.container}>
        <div id="container-fastlink"></div>
        {state.isLoading && (
          <div className={styles.overlay}>
            <div>
              <StyledSpinner />
              <button className={styles.actionButton} onClick={closeModal}>
                cancel
              </button>
            </div>
          </div>
        )}
        {state.isSyncing && (
          <div className={`${styles.overlay} ${styles.centered}`}>
            <div>
              <StyledSpinner />
              Importing holdings...
              <p className={styles.hint}>
                Please be patient. Depending on your brokerage this can take up to 2-3 minutes.
              </p>
            </div>
          </div>
        )}
        {state.isError && (
          <div className={`${styles.overlay} ${styles.centered}`}>
            An Error Occurred
            <p className="text-xs mt-4 w-64">{state.errorMessage}</p>
            <button className={styles.actionButton} onClick={close}>
              close
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};
